// frontend/src/pages/PersonaWorkshop/CoachPanel.tsx
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { RenderedMessage } from '@/components/chat/RenderedMessage'
import { useComposerStore } from '@/stores/composerStore'
import { useFeature } from '@/hooks/useFeature'
import { useSkillCatalog } from '@/hooks/useSkillCatalog'
import { useWorkflows } from '@/hooks/useWorkflows'
import { streamCoach } from '@/services/coachConnector'
import { validateProposal, applyProposal } from './applyCoachProposal'
import { proposalTargetLabel, proposalNewContent } from './coachProposalPresentation'
import { useCoachSession } from './useCoachSession'
import { workshopCopy } from './workshopCopy'
import type { CoachProposal, CoachTurn } from '@/types/personaCoach'
import type { Finding } from '@/utils/bundleFindings'
import type { PersonaBundleResponse } from '@/types/personaWizard'

interface Props {
  personaId: string
  personaName: string
  avatarUrl?: string | null
  bundle: PersonaBundleResponse | null
  findings: Finding[]
  seededRequest: { text: string; nonce: number } | null
  /**
   * Called once the seeded request has actually been sent, so the owner can
   * clear it. Required for correctness, not convenience: the in-component
   * nonce ref below only guards a single mount, and Radix unmounts this
   * panel whenever the Coach tab is inactive.
   */
  onSeedConsumed?: (nonce: number) => void
}

interface UiProposal { proposal: CoachProposal; applied: boolean; before?: string }
interface UiTurn { role: 'user' | 'assistant'; content: string; proposals: UiProposal[] }

/** Current text of the block a proposal targets, so the card can show a
 *  before→after. Only block edits have an in-field "before". */
function blockBefore(p: CoachProposal, bundle: PersonaBundleResponse | null): string | undefined {
  if (p.op !== 'updateBlock') return undefined
  return bundle?.blocks.find((b) => b.category === p.category)?.content ?? ''
}

export function CoachPanel({ personaId, personaName, avatarUrl, bundle, findings, seededRequest, onSeedConsumed }: Props) {
  const copy = workshopCopy(personaName)
  const { session, status, startFresh } = useCoachSession(personaId)
  const { data: skills = [] } = useSkillCatalog()
  // The workflows router rides workflow_engine; without it the catalog
  // query would 404 — coach workflow proposals just can't validate then.
  const workflowEngineOn = useFeature('workflow_engine')
  const { data: workflows = [] } = useWorkflows({ enabled: workflowEngineOn })
  const store = useComposerStore((s) => s)
  const serverBundle = useComposerStore((s) => s.serverBundle)
  // Applied edits live in the composer draft until the top-bar Save flushes it.
  // When no draft paths remain dirty, applied proposals are persisted ("Saved").
  const dirtyCount = store.dirtyPaths?.size ?? 0

  const [turns, setTurns] = useState<UiTurn[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Re-hydrate when the persisted session changes. Safe because useCoachSession
  // returns a referentially-stable `session` (React Query memoized data, or the
  // module-level EMPTY const) — do NOT change it to return an inline object literal.
  useEffect(() => {
    setSessionId(session.session_id)
    setTurns(session.turns.map((t: CoachTurn) => ({
      role: t.role, content: t.content,
      // Historical proposals: no live "before" snapshot — the card still shows
      // the target label + proposed content.
      proposals: (t.proposals ?? []).map((p) => ({ proposal: p, applied: false })),
    })))
  }, [session])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [turns])

  const send = async (text: string) => {
    const message = text.trim()
    if (!message || streaming) return
    setInput('')
    setTurns((prev) => [...prev,
      { role: 'user', content: message, proposals: [] },
      { role: 'assistant', content: '', proposals: [] }])
    setStreaming(true)
    const history = turns.map((t) => ({ role: t.role, content: t.content }))
    await streamCoach(personaId, {
      message, session_id: sessionId, bundle_snapshot: bundle, findings, history,
    }, {
      onToken: (tok) => setTurns((prev) => {
        const next = [...prev]; next[next.length - 1] = {
          ...next[next.length - 1], content: next[next.length - 1].content + tok }
        return next
      }),
      onProposal: (p) => setTurns((prev) => {
        const next = [...prev]; const last = next[next.length - 1]
        next[next.length - 1] = {
          ...last,
          proposals: [...last.proposals, { proposal: p, applied: false, before: blockBefore(p, bundle) }],
        }
        return next
      }),
      onComplete: (_full, sid) => { if (sid) setSessionId(sid); setStreaming(false) },
      onError: () => {
        // Surface the failure in the transcript — a silently-empty
        // assistant bubble reads as the coach ignoring the user.
        setTurns((prev) => {
          const next = [...prev]
          const last = next[next.length - 1]
          if (last?.role === 'assistant' && last.content === '') {
            next[next.length - 1] = { ...last, content: copy.coachStreamError }
          }
          return next
        })
        setStreaming(false)
      },
    })
  }

  // Seeded Review→Coach handoff: auto-send once per nonce (not per text —
  // re-clicking the same finding must resend, so identity, not content,
  // is what dedupes here).
  //
  // TWO guards, because they cover different lifetimes:
  //  - `seededSent` blocks a duplicate send within THIS mount (the effect
  //    re-runs when `status.available` flips).
  //  - `onSeedConsumed` tells the owner to drop the seed entirely. That is
  //    the one that matters: Radix's TabsContent has no `forceMount`
  //    (`present: forceMount || isSelected`), so leaving the Coach tab
  //    unmounts this panel and destroys `seededSent` while the owner's seed
  //    survives. Without consumption, coming back re-sent the message and
  //    bought a second billed coach call the user never asked for.
  const seededSent = useRef<number | null>(null)
  useEffect(() => {
    if (seededRequest && seededRequest.nonce !== seededSent.current && status?.available) {
      seededSent.current = seededRequest.nonce
      void send(seededRequest.text)
      onSeedConsumed?.(seededRequest.nonce)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededRequest, status?.available])

  const apply = (ti: number, pi: number) => {
    const proposal = turns[ti].proposals[pi].proposal
    applyProposal(proposal, store, {
      skills, workflows, bundle,
      savedBlocks: serverBundle?.blocks ?? [],
      liveBlocks: bundle?.blocks ?? [],
    })
    setTurns((prev) => {
      const next = prev.map((t) => ({ ...t, proposals: t.proposals.map((p) => ({ ...p })) }))
      next[ti].proposals[pi].applied = true
      return next
    })
  }

  if (status && !status.available) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="flex items-center gap-3">
          <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">💬</span>
          <span className="font-semibold text-foreground">{copy.coachTitle}</span>
        </div>
        <p className="text-sm text-muted-foreground">{copy.coachUnavailable}</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-3">
          <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">💬</span>
          <span>
            <span className="block font-semibold text-foreground">{copy.coachTitle}</span>
            <span className="block text-xs text-muted-foreground">Tune tone, rules, and skills in plain language.</span>
          </span>
        </span>
        <Button variant="ghost" size="sm" onClick={() => { seededSent.current = null; void startFresh() }}>
          Start fresh
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-auto rounded-lg bg-muted p-3 text-sm">
        {turns.length === 0 && <p className="text-muted-foreground">{copy.coachIntro}</p>}
        {turns.map((t, ti) => (
          <div key={ti} className={t.role === 'user' ? 'flex justify-end' : 'flex items-start gap-2'}>
            {t.role === 'assistant' && (
              avatarUrl
                ? <img src={avatarUrl} alt={personaName} className="h-6 w-6 rounded-full object-cover" />
                : <div className="flex h-6 w-6 items-center justify-center rounded-full bg-linear-to-br from-brand-accent to-brand-primary text-[11px] font-semibold text-white">{personaName.charAt(0).toUpperCase()}</div>
            )}
            <div className={t.role === 'user'
              ? 'max-w-[85%] rounded-2xl bg-muted px-3 py-2'
              : 'max-w-[85%] space-y-2'}>
              {t.content && (t.role === 'assistant'
                ? <div className="rounded-2xl bg-primary/10 px-3 py-2"><RenderedMessage content={t.content} /></div>
                : <div className="whitespace-pre-wrap">{t.content}</div>)}
              {t.proposals.map((up, pi) => {
                const v = validateProposal(up.proposal, {
                  skills, workflows, bundle,
                  savedBlocks: serverBundle?.blocks ?? [],
                  liveBlocks: bundle?.blocks ?? [],
                })
                const targetLabel = proposalTargetLabel(up.proposal, { skills, workflows })
                const newContent = proposalNewContent(up.proposal)
                return (
                  <Card key={pi} className="gap-1.5 rounded-lg p-2">
                    {/* WHERE the change lands, so a saved edit never looks like a no-op */}
                    <p className="text-xs font-semibold text-primary">{targetLabel}</p>
                    {newContent !== null && (
                      <div className="space-y-1">
                        {up.before ? (
                          <div>
                            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Currently</p>
                            <p className="max-h-16 overflow-auto whitespace-pre-wrap rounded-sm bg-muted px-2 py-1 text-xs text-muted-foreground">{up.before}</p>
                          </div>
                        ) : null}
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-success">{up.before ? 'New' : 'Proposed'}</p>
                          <p className="max-h-32 overflow-auto whitespace-pre-wrap rounded-sm bg-success/10 px-2 py-1 text-xs text-foreground">{newContent}</p>
                        </div>
                      </div>
                    )}
                    <p className="text-xs italic text-muted-foreground">{up.proposal.rationale}</p>
                    {up.applied ? (
                      <p className="text-xs font-medium text-success">
                        {dirtyCount === 0 ? '✓ Saved' : '✓ Applied to draft — Save to keep'}
                      </p>
                    ) : v.appliable ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => apply(ti, pi)}>Yes, update</Button>
                      </div>
                    ) : (
                      <p className="text-xs text-warning-strong">Can't apply: {v.reason}</p>
                    )}
                  </Card>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); void send(input) }} className="flex gap-2">
        <div className="flex-1">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={copy.coachPlaceholder}
            disabled={streaming}
          />
        </div>
        <Button type="submit" disabled={streaming || !input.trim()}>Send</Button>
      </form>
    </div>
  )
}
